import {Component, EventEmitter, Input, OnInit, OnDestroy, Output, SimpleChanges, OnChanges, ViewChild} from '@angular/core';
import {IonInput} from '@ionic/angular';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {LottiService} from '../../../_services/lotti.service';
import {Lotto} from '../../../_models/Lotto';
import {SaveMessage} from '../../../_helpers/save-message';
import {ArticoliService} from '../../../_services/articoli.service';
import {SearchResult} from '../../../_models/SearchResult';
import {Giacenza} from '../../../_models/Giacenza';
import {ArticoloEsolver} from '../../../_models/ArticoloEsolver';
import {ModalitaModificaLotto} from '../../../_models/AccountOrganizzazione';

@Component({
  selector: 'app-lotto-input',
  templateUrl: './lotto-input.component.html',
  styleUrls: ['./lotto-input.component.scss'],
  standalone: false
})
export class LottoInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() readonly = false;
  @Input() codArt: string;
  @Input() varianteArt: string;
  @Input() rifLotto = '';
  @Output() rifLottoChange = new EventEmitter<string>();
  @Input() checkRifLotto = '';
  @Input() codProdotto = '';
  @Input() rifMagazzino = '';
  @Output() rifMagazzinoChange = new EventEmitter<string>();
  @Input() rifMagArea = '';
  @Output() rifMagAreaChange = new EventEmitter<string>();
  @Input() rifGiacenza = 0;
  @Output() rifGiacenzaChange = new EventEmitter<number>();
  @Input() giacenza: Giacenza;
  @Output() giacenzaChange = new EventEmitter<Giacenza>();
  @Input() checkOnInit = true;
  @Input() labelText = 'lotto';
  @Input() risorsaId = -1;
  @Input() lottoGiacenza = '';
  lotto: Lotto = {
    codArt: '',
    varianteArt: '',
    rifLottoAlfa: '',
    validForProdotto: true
  };
  giacenze: Giacenza[] = [];
  showGiacenza = false;

  /**
   * Articolo di ZVIEWARTICOLI, letto una volta sola. Governa due cose nella creazione del lotto:
   * CodGruppoCodifLotti (chi sceglie il codice) e MagGestDataScadLotti (se serve la scadenza).
   * Null quando non e' stato letto: in quel caso vale il comportamento storico.
   */
  articolo: ArticoloEsolver | null = null;

  /**
   * L'articolo appartiene a un gruppo di codifica lotti: il codice lo genera ESOLVER e l'operatore
   * non puo' sceglierlo. La digitazione resta possibile — serve a TROVARE un lotto esistente, ed e'
   * il modo in cui funziona il lettore barcode — ma in creazione il codice digitato non viaggia.
   */
  get codificaGestitaDaEsolver(): boolean {
    return !!this.articolo?.codGruppoCodifLotti?.trim();
  }

  /**
   * Testo guida del campo. I tre casi storici restano parola per parola; se ne aggiunge uno solo,
   * per dire che il codice non si inventa: con un gruppo di codifica si puo' cercare un lotto
   * esistente, ma quello nuovo lo numera ESOLVER e il "+" ignora quanto digitato.
   *
   * L'ordine conta. inputBloccato viene PRIMA della codifica: in Fisso (e in ConSblocco finche' il
   * lucchetto e' chiuso) il campo non e' digitabile, quindi "Cerca un lotto" sarebbe una bugia.
   * I due assi non si sovrappongono: la preferenza operatore governa SE si puo' digitare, il gruppo
   * di codifica governa se quel che si digita viene usato per creare.
   */
  get placeholderLotto(): string {
    // Con readonly la label esterna e' vuota: qui resta l'unica cosa che nomina il campo.
    if (this.readonly) return this.labelText;
    if (this.inputBloccato) return `Usa i pulsanti per il ${this.labelText}`;
    if (this.codificaGestitaDaEsolver) return `Cerca un ${this.labelText}, o crea con +`;
    return `Seleziona un ${this.labelText}`;
  }
  isValid = -1;
  loading = false;

  @ViewChild('lottoInputRef') lottoInputRef: IonInput;

  /** Sblocco effimero della modalità ConSblocco: si richiude al blur (vedi onLottoBlur). */
  lottoUnlocked = false;

  /** Timer di richiusura pendente: va annullato se nel frattempo arriva uno sblocco. */
  private richiusuraTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Modalità configurata dall'operatore. Letta qui e non passata da fuori: il componente ha già
   * OrganizzazioniService, e così nessuno dei ~30 chiamanti di app-lotto-input deve cambiare.
   */
  get modalitaModificaLotto(): ModalitaModificaLotto {
    return this.organizzazioniService.tipoModificaLotto;
  }

  /**
   * Digitazione bloccata dall'opzione operatore. NON copre `readonly`, che è l'asse storico e
   * diverso — "sola lettura totale", pulsanti nascosti — e che qui cortocircuita: dove il
   * chiamante impone readonly, la preferenza dell'operatore non ha voce.
   */
  get inputBloccato(): boolean {
    if (this.readonly) {
      return false;
    }
    if (this.modalitaModificaLotto === ModalitaModificaLotto.Fisso) {
      return true;
    }
    return this.modalitaModificaLotto === ModalitaModificaLotto.ConSblocco && !this.lottoUnlocked;
  }

  /** Unione dei due assi: è l'unico valore che finisce sul [readonly] dell'ion-input. */
  get inputReadonly(): boolean {
    return this.readonly || this.inputBloccato;
  }

  get showLucchetto(): boolean {
    return !this.readonly && this.modalitaModificaLotto === ModalitaModificaLotto.ConSblocco;
  }

  constructor(
    private saveMessage: SaveMessage,
    private organizzazioniService: OrganizzazioniService,
    private lottiService: LottiService,
    private articoliService: ArticoliService) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Il chiamante ha ripuntato il componente su un altro articolo (lo fanno movimenti-rettifica e
    // inventario-manuale): senza riallineare qui, lotto.codArt e l'articolo in cache resterebbero
    // quelli di prima e il vincolo di codifica verrebbe letto dall'articolo sbagliato.
    if (changes['codArt'] && !changes['codArt'].firstChange) {
      this.lotto.codArt = changes['codArt'].currentValue as string;
      this.caricaArticolo();
    }

    // firstChange = true significa che è la prima assegnazione (ngOnInit non è ancora girato)
    if (changes['rifLotto'] && !changes['rifLotto'].firstChange) {
      const newValue = changes['rifLotto'].currentValue as string;

      // Aggiorna lo stato interno
      this.lotto.rifLottoAlfa = newValue;

      // Esempio: ri-verifica il lotto se il parent cambia il valore
      if (newValue && newValue.length > 0) {
        this.getLotto(false);
      } else {
        this.isValid = -1;
      }
    }
  }

  ngOnInit() {
    this.lotto.codArt = this.codArt;
    this.lotto.varianteArt = this.varianteArt;
    this.lotto.rifLottoAlfa = this.rifLotto;
    this.lotto.prodotto = this.codProdotto;
    this.caricaArticolo();
    if (this.rifLotto && this.rifLotto.length > 0 && this.checkOnInit) {
      this.getLotto(false);
    }
  }

  /**
   * Legge l'articolo da ZVIEWARTICOLI. Non blocca nulla e non segnala errori: se non si riesce a
   * leggerlo si ricade sul comportamento storico, e un eventuale vincolo lo fa valere ESOLVER
   * rifiutando la creazione con il proprio messaggio.
   */
  private caricaArticolo() {
    this.articolo = null;
    // In readonly i pulsanti sono nascosti e il placeholder non usa il flag: la lettura sarebbe
    // sprecata. Conta: app-lotto-input compare una volta per RIGA nelle liste materiali.
    if (this.readonly || !this.lotto.codArt) return;
    const codArt = this.lotto.codArt;
    this.articoliService.getArticoloEsolverByCodArt(this.organizzazioniService.organizzazioneId, codArt)
      .then((articolo: ArticoloEsolver | null) => {
        // Il codArt puo' essere cambiato mentre la lettura era in volo (ngOnChanges lo ripunta su
        // un altro articolo): una risposta vecchia non deve sovrascrivere quella giusta.
        if (codArt === this.lotto.codArt) this.articolo = articolo;
      })
      // Qui si tira dritto di proposito: l'anagrafica serve solo a decidere un dettaglio della
      // creazione, e se il vincolo c'era davvero e' ESOLVER a rifiutare con il proprio messaggio.
      .catch(() => this.articolo = null);
  }

  toggleLottoLock() {
    // Annullare la richiusura pendente è la parte che fa funzionare il lucchetto, non una pulizia.
    // Il campo readonly resta focalizzabile di proposito, quindi l'operatore spesso ci tappa sopra
    // prima di cercare il lucchetto: il tap sul lucchetto produce allora un blur (che schedula la
    // richiusura) e SOLO DOPO il click. Senza questo clearTimeout la richiusura girerebbe dopo lo
    // sblocco e lo annullerebbe — e poiché qui sotto rimettiamo il fuoco nel campo, ogni tap
    // successivo ripeterebbe la stessa sequenza: il lucchetto non aprirebbe mai.
    this.cancellaRichiusura();

    this.lottoUnlocked = !this.lottoUnlocked;
    if (this.lottoUnlocked) {
      // Il fuoco va nel campo appena si apre: altrimenti servirebbe un secondo tap.
      setTimeout(() => this.lottoInputRef?.setFocus(), 0);
    }
  }

  ngOnDestroy(): void {
    this.cancellaRichiusura();
  }

  private cancellaRichiusura() {
    if (this.richiusuraTimer !== null) {
      clearTimeout(this.richiusuraTimer);
      this.richiusuraTimer = null;
    }
  }

  onLottoBlur() {
    if (this.modalitaModificaLotto !== ModalitaModificaLotto.ConSblocco) {
      return;
    }
    // Il setTimeout non è cosmesi: in Ionic 7 ionChange viene emesso DURANTE la gestione del blur.
    // Rimettere readonly in modo sincrono qui rischia di sopprimere quell'ionChange, e con esso la
    // verifica del lotto appena digitato. Il macrotask garantisce l'ordine.
    this.cancellaRichiusura();
    this.richiusuraTimer = setTimeout(() => {
      this.lottoUnlocked = false;
      this.richiusuraTimer = null;
    }, 0);
  }

  setDefLotto() {
    this.lotto.rifLottoAlfa = this.checkRifLotto;
    // Il caricamento della giacenza serve solo quando il campo è bloccato: lì questo pulsante è
    // l'unico sostituto della digitazione e deve produrne gli stessi effetti, incluso l'unico punto
    // che emette rifMagazzino, rifMagArea, rifGiacenza e giacenza al chiamante. Con il campo libero
    // resta il comportamento storico: caricare la giacenza anche lì aggiungerebbe righe di consumo
    // non richieste (vedi selGiacenza in interfaccia-manuale-lotto-materiale-list) a chi non ha
    // attivato nulla.
    this.getLotto(this.inputBloccato);
  }

  createLotto() {
    this.loading = true;
    // Si inviano i soli campi che identificano il lotto da creare. this.lotto e' a vita lunga e
    // ngModel ne tocca il solo rifLottoAlfa: dopo una verifica riuscita contiene il record letto da
    // ESOLVER (data di apertura, anagrafica di riferimento, annotazione, blocco), che per un codice
    // nuovo sarebbe residuo del lotto precedente. La data di apertura la assegna ESOLVER.
    const nuovoLotto: Lotto = {
      codArt: this.lotto.codArt,
      varianteArt: this.lotto.varianteArt,
      // Con un gruppo di codifica il codice lo genera ESOLVER, e il modo per chiederglielo e'
      // proprio non mandarlo: LottiService.CreateLotto azzera i riferimenti quando arriva vuoto.
      rifLottoAlfa: this.codificaGestitaDaEsolver ? '' : this.lotto.rifLottoAlfa
    };
    // Scadenza obbligatoria per l'articolo: il backend non mette piu' alcun default, decide qui.
    // Costruita in UTC di proposito: `new Date(2099, 11, 31)` e' mezzanotte LOCALE e verrebbe
    // serializzata come 2099-12-30T23:00:00Z, cioe' con il giorno gia' sbagliato in partenza.
    if (this.articolo?.magGestDataScadLotti === 1) {
      nuovoLotto.dataScadenzaLotto = new Date(Date.UTC(2099, 11, 31));
    }
    if (this.codificaGestitaDaEsolver) {
      // Il campo si svuota al click, non alla risposta: quel che c'era era un termine di ricerca,
      // non il codice che verra' creato, e lasciarlo li' durante l'attesa lascerebbe credere che
      // ESOLVER stia registrando proprio quello. L'emissione serve perche' il binding [(rifLotto)]
      // del chiamante non resti sul digitato; il codice vero arriva in fondo, dalla risposta.
      this.lotto.rifLottoAlfa = '';
      this.rifLottoChange.emit('');
    }
    this.lottiService.createLottoAsync(this.organizzazioniService.organizzazioneId, nuovoLotto)
      .then((_lotto: Lotto) => {
        if (!_lotto.codArt) {
          this.saveMessage.messageError("Problemi durante la creazione del lotto.");
          this.isValid = -1;
          return false;
        }
        this.saveMessage.messageInfo('Lotto creato con successo.');
        this.lotto = _lotto;
        this.lotto.validForProdotto = true;
        this.isValid = 1;
        this.rifLottoChange.emit(this.lotto.rifLottoAlfa);
      })
      .catch(() => this.saveMessage.messageError("Errore durante la creazione del lotto."))
      .finally(() => this.loading = false);
  }

  getLotto(loadGiacenza: boolean) {
    if (!this.lotto.codArt) {
      return false;
    }
    this.loading = true;
    this.isValid = -1;
    this.rifLottoChange.emit(this.lotto.rifLottoAlfa);
    this.lottiService.verificaLottoAsync(this.organizzazioniService.organizzazioneId, this.lotto)
      .then((_lotto: Lotto) => {
        if (_lotto.codArt === this.lotto.codArt) {
          this.lotto = _lotto;
          this.isValid = 1;
          this.rifLottoChange.emit(this.lotto.rifLottoAlfa);
          if (loadGiacenza) {
            this.getGiacenza(true);
          }
          return;
        }
        if (_lotto.rifLottoAlfa === this.lotto.rifLottoAlfa) {
          this.isValid = 0;
          this.saveMessage.messageError("Lotto trovato ma articolo diverso.");
        }
      })
      .catch(() => this.saveMessage.messageError("Errore durante la ricerca del lotto."))
      .finally(() => this.loading = false);
  }

  getGiacenza(onlyLoad: boolean) {
    if (!onlyLoad) {
      this.showGiacenza = !this.showGiacenza;
      if (!this.showGiacenza) {
        this.giacenze = [];
        return false;
      }
    }
    this.loading = true;
    this.articoliService.getGiacenza(this.organizzazioniService.organizzazioneId, this.lotto.codArt, '', '', this.lottoGiacenza, this.risorsaId)
      .then((_giacenze: SearchResult<Giacenza>) => {
        if (_giacenze.totalCount > 0) {
          if (!onlyLoad) {
            this.giacenze = _giacenze.result;
          } else {
            const giacenza = _giacenze.result
              .filter((_giacenza: Giacenza) => _giacenza.lotto === this.lotto.rifLottoAlfa);
            if (giacenza.length > 0) {
              this.selectGiacenza(giacenza[0], false);
            }
          }
        }
      })
      .catch(() => this.saveMessage.messageError("Problemi con il caricamento della giacenza dell'articolo."))
      .finally(() => this.loading = false);
  }

  selectGiacenza(giacenza: Giacenza, checkLotto: boolean) {
    this.lotto.rifLottoAlfa = giacenza.codLottoAlfa;
    this.rifMagazzinoChange.emit(giacenza.codMag);
    this.rifMagAreaChange.emit(giacenza.codAreaMag);
    this.rifGiacenzaChange.emit(giacenza.giacenza);

    this.giacenzaChange.emit(giacenza);

    if (checkLotto) {
      this.giacenze = [];
      this.showGiacenza = false;
      this.getLotto(false);
    }
  }
}
